/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable arrow-body-style */
/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import {
    BehaviorSubject,
    catchError,
    map,
    Observable,
    of,
    ReplaySubject,
    shareReplay,
    switchMap,
    take,
    tap,
    throwError,
    timeout,
} from 'rxjs';
import { CierreVeterinariaDTO, Consulta, RequestConsultaReagandarCita, RequestRespuestaDeSeguridad, ResponseRespuestaDeSeguridad } from './consulta.types';
import { RequestConsultaConfirmarCita } from './consulta.types';
import { environment } from 'src/environments/environment';
import { listaConsultasMocks } from 'src/app/shared/mocks/consultaMoks';
import { AtencionService } from '../atencion/atencion.service';
import { DatosUsuarioParaActualizar, User } from '../user/user.types';
import { ResponseValidateOtp } from '../message-service/message-service.types';

@Injectable({
    providedIn: 'root',
})
export class ConsultaService {
    private apiUrl: string = environment.BASE_URL;
    private _consulta: BehaviorSubject<Consulta> = new BehaviorSubject<Consulta>(null);
    private _consultas: BehaviorSubject<Consulta[]> = new BehaviorSubject<Consulta[]>(null);
    private _consultasUsuarioVet: BehaviorSubject<Consulta[]> = new BehaviorSubject<Consulta[]>(null);
    private _consultasUsuarioClient: BehaviorSubject<Consulta[]> = new BehaviorSubject<Consulta[]>(null);
    private _sseConnectionStatus: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    
    // Caché para el estado de anuncio por veterinaria
    private _anunciosCache: Map<number, boolean> = new Map();
    private _anunciosTimestamp: Map<number, number> = new Map();
    private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutos en millisegundos
    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _atencionService: AtencionService,
        private zone: NgZone
    ) { }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    //////////////////////////////////////////////////////////////////////////////////////////////////

    /**
     * Getter for Consulta
     */

    set Consulta(consulta: Consulta) {
        this._consulta.next(consulta);
    }

    get Consulta$(): Observable<Consulta> {
        return this._consulta.asObservable();
    }

    /**
     * Getter for Consultas
     */
    get Consultas$(): Observable<Consulta[]> {
        return this._consultas.asObservable();
    }
    get _consultasUsuarioVet$(): Observable<Consulta[]> {
        return this._consultasUsuarioVet.asObservable();
    }
    get _consultasUsuarioClient$(): Observable<Consulta[]> {
        return this._consultasUsuarioClient.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------
    /**
     * Get SSE
     */
    private sessionId = this.generateSessionId();  
    private eventSources: Map<number, EventSource> = new Map();
    
    get sseStatus$(): Observable<boolean> {
        return this._sseConnectionStatus.asObservable();
    }

    getAppointmentsStream(idVeterinaria: number): Observable<any> {
        return new Observable(observer => {
            this.sessionId = this.generateSessionId();
            this.closeConnection(idVeterinaria);
            this._sseConnectionStatus.next(false);
            let hasReceivedEvent = false;
            const markConnected = () => {
                if (!hasReceivedEvent) {
                    hasReceivedEvent = true;
                    this._sseConnectionStatus.next(true);
                }
            };
            const url = `${this.apiUrl}suscriptor/appointments/stream/${idVeterinaria}?sessionId=${this.sessionId}`;
            const eventSource = new EventSource(url);
            this.eventSources.set(idVeterinaria, eventSource);
            eventSource.onopen = () => {
                this.zone.run(() => {
                    markConnected(); // Marcar conectado apenas se abre el stream (estado pending)
                    observer.next([]);
                });
            };
            const messageHandler = (event: MessageEvent) => {
                this.zone.run(() => {
                    try {
                        markConnected();
                        const data: Consulta[] = JSON.parse(event.data);
                        observer.next(data);
                    } catch (error) {
                        console.error('Error parsing SSE data:', error, event.data);
                        observer.error(error);
                    }
                });
            };
            const heartbeatHandler = () => {
                this.zone.run(() => {
                    markConnected();
                });
            };

            const errorHandler = (error: Event) => {
                this.zone.run(() => {
                    console.error('Error en conexión SSE:', error);
                    this._sseConnectionStatus.next(false);
                    observer.error(error);
                    // Reconexión automática después de 5 segundos
                    setTimeout(() => this.getAppointmentsStream(idVeterinaria).subscribe(), 10000);
                });
            };

            eventSource.addEventListener('consultas-actualizadas', messageHandler);
            eventSource.addEventListener('heartbeat', heartbeatHandler as EventListener);
            eventSource.onerror = errorHandler;
            return () => {
                eventSource.removeEventListener('consultas-actualizadas', messageHandler);
                eventSource.removeEventListener('heartbeat', heartbeatHandler as EventListener);
                this.closeConnection(idVeterinaria);
            };
            }).pipe(
                shareReplay({bufferSize: 1, refCount: true}),
                catchError(error => {
                    console.error('Error en el flujo, reintentando...', error);
                    return throwError(error);
                }));
    }
    private closeConnection(idVeterinaria: number): void {
        const eventSource = this.eventSources.get(idVeterinaria);
        if (eventSource) {
            eventSource.close();
            this.eventSources.delete(idVeterinaria);
        }
        this._sseConnectionStatus.next(false);
    }
    // Generar nuevo ID al crear el servicio
    private generateSessionId(): string {
        return Math.random().toString(36).substring(2) + Date.now().toString(36);
    }
    /**
     * Get Consulta by id
     */
    solicitarOtpParaActualizarLosDatos(correo: String) {
        return this._httpClient.post<ResponseValidateOtp>(this.apiUrl + 'otp/opt-send-validate-data-update', { "correo": correo })
            .pipe(
                map(response => {
                    console.log('Respuesta:', response); // Puedes hacer algo con la respuesta aquí
                    return response;
                }),
                catchError((error: HttpErrorResponse) => {
                    let errorMessage = 'Error desconocido';

                    if (error.status === 409) {
                        errorMessage = error.error.message || 'Conflicto en la solicitud';
                    } else {
                        // Manejar otros errores (e.g., 500)
                        console.error('Error inesperado:', error);
                    }

                    return throwError(() => new Error(errorMessage));
                })
            );
    }

    verificarOtpParaActualizarLosDatos(correo: string, otp: string) {
        return this._httpClient.post<ResponseValidateOtp>(this.apiUrl + '/otp/opt-validate-data-update',
            {
                "correo": correo,
                "otp": otp
            }).pipe(
                map(response => {
                    console.log('varificacion:', response); // Puedes hacer algo con la respuesta aquí
                    return response;
                }),
                catchError((error: HttpErrorResponse) => {
                    console.error('Error al obtener los datos:', error);
                    // Manejar el error de forma personalizada
                    return throwError(() => new Error('Ocurrió un error al solicitar los datos'));
                })
            );
    }

    verificarPreguntaDeSeguridadParaActualizarLosDatos(idUsuario: number, respuesta: string) {
        return this._httpClient.post<any>(this.apiUrl + 'citas/usuario/respuesta-seguridad', {
            "idUsuario": idUsuario,
            "respuesta": respuesta
        })
            .pipe(
                map(response => {
                    return response;
                }),
                catchError((error: HttpErrorResponse) => {
                    return of({ error: error.message });
                })
            );

    }
    actualizarDatosDelUsuario(confirmarCita: DatosUsuarioParaActualizar): Observable<any> {
        return this._httpClient.put<any>(this.apiUrl + 'citas/reactive/usuario-actualizar/' + confirmarCita.id, confirmarCita)
            .pipe(
                map(consulta => {
                    // Procesar la respuesta exitosa (consulta)
                    console.log('Consulta actualizada:', consulta);
                    return consulta;
                }),
                catchError((error: HttpErrorResponse) => {
                    // Manejar el error de la petición
                    console.error('Error al actualizar la consulta:', error);
                    return of({ error: error.message });
                })
            );
    }

    validaPreguntaDeseguridad(request: RequestRespuestaDeSeguridad) {
        return this._httpClient.post<ResponseRespuestaDeSeguridad>(this.apiUrl + 'citas/usuario/respuesta-seguridad', request);
    }
    getConsultasByUsuarioClient(idUserClient): Observable<Consulta[]> {
        return this._httpClient.get<Consulta[]>(this.apiUrl + 'citas/atenciones/citas-cliente-full-estados/' + idUserClient).pipe(
            map((consultas: Consulta[]) => {
                return [...consultas].reverse();
            }),
            switchMap((consultas: Consulta[]) => {
                this._consultasUsuarioClient.next(consultas);
                return of(consultas);
            }),
            catchError((error: any) => {
                return of([]);
            })
        );
    }

    getConsultasByUsuarioVet(idUserVet): Observable<Consulta[]> {

        return this._httpClient.get<Consulta[]>(this.apiUrl + 'suscriptor/consulta-fullestado-veterinaria/' + idUserVet).pipe(
            switchMap((consultas: Consulta[]) => {
                this._consultasUsuarioVet.next(consultas);
                return of(consultas);
            }),
            catchError((error: any) => {
                return of([]);
            }));
    }

    getConsultas(): Observable<Consulta[]> {
        return this._httpClient.get<Consulta[]>(this.apiUrl + 'suscriptor/consulta-estado-usuario-suscriptor').pipe(
            switchMap((Consultas: Consulta[]) => {
                this._consultas.next(Consultas);
                return of(Consultas);
            }),
            catchError((error: any) => {
                return of([]);
            }));
    }

    getConsultaById(id: string): Observable<Consulta> {
        return this._httpClient.get<Consulta>(this.apiUrl + '/' + id).pipe(
            take(1),
            switchMap((Consulta: Consulta) => {
                if (!Consulta) {
                    return throwError(
                        'Could not found Consulta with id of ' + id + '!'
                    );
                }
                this._consulta.next(Consulta);
                this.updateConsultaInList(Consulta);
                return of(Consulta);
            })
        );
    }

    aceptarCita(confirmarCita: RequestConsultaConfirmarCita, consulta: Consulta): Observable<any> {
        return this._httpClient.put<any>(this.apiUrl + 'suscriptor/consultas/aceptar-cita/' + confirmarCita.idCita, confirmarCita)
            .pipe(
                map(consulta => {
                    // Procesar la respuesta exitosa (consulta)
                    console.log('Consulta actualizada:', consulta);
                    return consulta;
                }),
                catchError((error: HttpErrorResponse) => {
                    // Manejar el error de la petición
                    console.error('Error al actualizar la consulta:', error);
                    return of({ error: error.message });
                })
            );
    }
    /*
Tiene anuncio
 */

    tieneAnuncio(idVeterinaria: any): Observable<any> {
        const veterinariaId = Number(idVeterinaria);
        const now = Date.now();
        
        // Verificar si tenemos datos en caché y si están vigentes
        if (this._anunciosCache.has(veterinariaId) && this._anunciosTimestamp.has(veterinariaId)) {
            const timestamp = this._anunciosTimestamp.get(veterinariaId);
            const isValid = (now - timestamp) < this.CACHE_DURATION;
            
            if (isValid) {
                console.log('📦 Usando caché para anuncio veterinaria:', veterinariaId);
                return of(this._anunciosCache.get(veterinariaId));
            }
        }

        // Si no hay caché válido, hacer petición HTTP
        console.log('🌐 Petición HTTP para anuncio veterinaria:', veterinariaId);
        return this._httpClient.get<boolean>(this.apiUrl + 'suscriptor/veterinaria/anuncio/' + veterinariaId)
            .pipe(
                map(anuncio => {
                    // Guardar en caché
                    this._anunciosCache.set(veterinariaId, anuncio);
                    this._anunciosTimestamp.set(veterinariaId, now);
                    return anuncio;
                }),
                catchError((error: HttpErrorResponse) => {
                    // Manejar el error de la petición
                    console.error('Error en conseguir datos de anuncio:', error);
                    return of({ error: error.message });
                })
            );
    }
    modificarAnuncio(dto: CierreVeterinariaDTO, anuncio: Boolean): Observable<any> {
        // messages/cierre-temporal
        let url = anuncio ? 'messages/apertura' : 'messages/cierre-temporal';
        return this._httpClient.post<boolean>(this.apiUrl + 'otp/' + url, dto)
            .pipe(
                map(response => {
                    // Limpiar caché cuando se modifica el anuncio
                    const veterinariaId = Number(dto.idVeterinaria);
                    if (!isNaN(veterinariaId)) {
                        this.clearAnuncioCache(veterinariaId);
                        console.log('🗑️ Caché limpiado para veterinaria:', veterinariaId);
                    }
                    return response;
                }),
                catchError((error: HttpErrorResponse) => {
                    // Manejar el error de la petición
                    console.error('Error en modificar anuncio:', error);
                    return of({ error: error.message });
                })
            );
    }
    /*
    Pagar Cita
     */

    confirmarPago(confirmarCita: RequestConsultaConfirmarCita, consulta: Consulta): Observable<any> {
        return this._httpClient.put<any>(this.apiUrl + 'suscriptor/consultas/pago-cita/' + confirmarCita.idCita, confirmarCita).pipe(
            take(1),
            switchMap((response: any) => {
                this._consulta.next(consulta);
                this.updateConsultaInList(consulta);
                return of(response);
            })
        );
    }
    /**
    * Reagandar Cita o Consulta
    */

    aceptarReagendamiento(Consulta: RequestConsultaReagandarCita): Observable<any> {
        return this._httpClient.put<any>(this.apiUrl + 'suscriptor/consulta/reagendar', Consulta)
            .pipe(
                map(consulta => {
                    // Procesar la respuesta exitosa (consulta)
                    console.log('Consulta actualizada:', consulta);
                    return consulta;
                }),
                catchError((error: HttpErrorResponse) => {
                    // Manejar el error de la petición
                    console.error('Error al actualizar la consulta:', error);
                    return of({ error: error.message });
                })
            );
    }

    solicitarReagendamiento(Consulta: RequestConsultaReagandarCita): Observable<any> {
        return this._httpClient.put<any>(`${this.apiUrl}suscriptor/consulta/solicitar-reagendar`, Consulta)
            .pipe(
                map(consulta => {
                    // Procesar la respuesta exitosa (consulta)
                    console.log('Consulta actualizada:', consulta);
                    return consulta;
                }),
                catchError((error: HttpErrorResponse) => {
                    // Manejar el error de la petición
                    console.error('Error al actualizar la consulta:', error);
                    return of({ error: error.message });
                })
            );
    }
    cancelarCita(consulta: Consulta): Observable<any> {
        return this._httpClient.put<any>(this.apiUrl + 'suscriptor/consultas/rechazar-cita/' + consulta.consultaId, {}).pipe(
            take(1),
            switchMap((response: any) => {
                consulta.consultaEstadoCita = 4;
                this._consulta.next(consulta);
                this.updateConsultaInList(consulta);
                return of(response);
            })
        );
    }

    updateConsulta(Consulta: Consulta): Observable<Consulta> {
        return this._httpClient.put<Consulta>(this.apiUrl + '/', Consulta).pipe(
            take(1),
            switchMap((Consulta: Consulta) => {
                this._consulta.next(Consulta);
                this.updateConsultaInList(Consulta);
                return of(Consulta);
            })
        );
    }
    deleteConsulta(id: string): Observable<boolean> {
        return this._httpClient.delete<any>(this.apiUrl + id).pipe(
            take(1),
            switchMap((data: any) => {
                this.updateConsultaInList(this.getConsultaByIdInList(+id));
                return of(data);
            })
        );
    }
    /**
     * Create Consulta
     */


    createConsulta(Consulta: Consulta): Observable<Consulta> {
        return this._httpClient.post<any>(this.apiUrl + 'suscriptor/consultas', Consulta)
            .pipe(
                map(consulta => {
                    this._consultas.next([Consulta, ...this._consultas.value]);
                    return consulta;
                }),
                catchError((error: HttpErrorResponse) => {
                    // Manejar el error de la petición
                    console.error('Error al actualizar la consulta:', error);
                    return of({ error: error.message });
                })
            );
    }

    /*
     *
     *Funciones auxiliares
     */

    // Función para actualizar un objeto Consulta en la lista
    updateConsultaInList(updatedConsulta: Consulta): void {
        const currentConsultas = this._consultas.getValue();

        if (currentConsultas) {
            const indice = currentConsultas.findIndex(consult => consult.consultaId === updatedConsulta.consultaId);
            if (indice !== -1) {
                currentConsultas[indice] = updatedConsulta;
                this._consultas.next([...currentConsultas]); // Emite una nueva lista actualizada
            } else {
                console.log(`No se encontró ningún Consulta con el ID ${updatedConsulta.consultaId}.`);
            }
        } else {
            console.log('La lista de consultas está vacía.');
        }
    }

    getConsultaByIdInList(id: number): Consulta | null {
        const currentConsultas = this._consultas.value;

        if (currentConsultas) {
            this._consultas.value.map((Consulta: Consulta) => {
                if (Consulta.consultaId === id) {
                    return Consulta;
                }
            });

            return null;
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Métodos de gestión de caché para anuncios
    // -----------------------------------------------------------------------------------------------------

    /**
     * Limpiar caché de anuncio para una veterinaria específica
     */
    clearAnuncioCache(idVeterinaria: number): void {
        this._anunciosCache.delete(idVeterinaria);
        this._anunciosTimestamp.delete(idVeterinaria);
    }

    /**
     * Limpiar todo el caché de anuncios
     */
    clearAllAnunciosCache(): void {
        this._anunciosCache.clear();
        this._anunciosTimestamp.clear();
    }

    /**
     * Forzar recarga del anuncio desde el servidor
     */
    refreshAnuncio(idVeterinaria: number): Observable<any> {
        this.clearAnuncioCache(idVeterinaria);
        return this.tieneAnuncio(idVeterinaria);
    }

    /**
     * Verificar si el caché es válido para una veterinaria
     */
    isCacheValid(idVeterinaria: number): boolean {
        if (!this._anunciosTimestamp.has(idVeterinaria)) {
            return false;
        }
        const timestamp = this._anunciosTimestamp.get(idVeterinaria);
        return (Date.now() - timestamp) < this.CACHE_DURATION;
    }


}
